import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MODES, type Mode } from '../../types/agent';

/** Tool-policy mode selector (#5): a compact mono pill living in the composer's hint row, opening a
 *  small popover (upwards by default) with one-line descriptions. */
export function ModeSwitch({ mode, onChange, disabled, placement = 'up' }: { mode: Mode; onChange: (m: Mode) => void; disabled?: boolean; placement?: 'up' | 'down' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={t(`mode.${mode}Hint`)}
        aria-label={t('mode.label')}
        className={`flex h-[22px] items-center gap-1 rounded-[5px] border px-1.5 font-mono text-[11px] transition-colors hover:bg-hover-soft disabled:opacity-50 ${open ? 'border-line bg-hover-soft text-ink' : 'border-line-soft text-muted'}`}
      >
        {t(`mode.${mode}`).toLowerCase()}
        <span className="text-[9px] text-faint">{placement === 'up' ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className={`absolute z-20 flex w-[210px] ${placement === 'up' ? 'bottom-[calc(100%+6px)] left-0' : 'right-0 top-[calc(100%+6px)]'} animate-pc-in-fast flex-col rounded-md border border-line bg-white p-1 shadow-[0_6px_20px_rgba(0,0,0,.08)]`}>
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                onChange(m);
                setOpen(false);
              }}
              className={`flex flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-left hover:bg-hover ${m === mode ? 'bg-hover' : ''}`}
            >
              <span className="flex w-full items-center justify-between font-mono text-[11px] text-ink">
                {t(`mode.${m}`).toLowerCase()}
                {m === mode && <span className="text-accent">✓</span>}
              </span>
              <span className="text-[11px] leading-snug text-faint">{t(`mode.${m}Hint`)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
